import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  Play,
  LogIn,
  UserCircle,
  UserPlus,
  LayoutDashboard,
  FolderOpen,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  GraduationCap,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";

const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_384e3b79-7448-4187-9e64-5e6eae223b81/artifacts/ezeifvlu_Untitled%20design%20%2823%29.mp4";

const QUICK_ADD_IMG =
  "https://customer-assets.emergentagent.com/job_384e3b79-7448-4187-9e64-5e6eae223b81/artifacts/t5p3uob1_image.png";

const TrainingStep = ({ number, title, description, tips, icon: Icon, image }) => {
  const [open, setOpen] = useState(false);
  const { isDarkMode } = useTheme();

  return (
    <Card
      className={`border transition-all duration-200 ${
        isDarkMode
          ? "bg-zinc-900 border-zinc-800 hover:border-emerald-700"
          : "bg-white border-slate-200 hover:border-[#84c77f]"
      }`}
      data-testid={`training-step-${number}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left"
        data-testid={`training-step-toggle-${number}`}
      >
        <CardHeader className="flex flex-row items-center gap-4 py-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#357c2a] to-[#265324] flex items-center justify-center text-white font-bold text-sm">
            {number}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Icon className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? "text-emerald-400" : "text-[#357c2a]"}`} />
              <CardTitle className={`text-base ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                {title}
              </CardTitle>
            </div>
            <p className={`text-sm mt-1 ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
              {description}
            </p>
          </div>
          <div className={isDarkMode ? "text-zinc-500" : "text-slate-400"}>
            {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
      </button>

      {open && (
        <CardContent className="pt-0 pb-4 px-6">
          <div className={`ml-14 space-y-3 border-t pt-4 ${isDarkMode ? "border-zinc-800" : "border-slate-100"}`}>
            {image && (
              <div className="rounded-lg overflow-hidden border mb-3" style={{ borderColor: isDarkMode ? "#333" : "#e2e8f0" }}>
                <img src={image} alt={title} className="w-full h-auto" loading="lazy" />
              </div>
            )}
            {tips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDarkMode ? "text-emerald-500" : "text-[#84c77f]"}`} />
                <p className={`text-sm leading-relaxed ${isDarkMode ? "text-zinc-300" : "text-slate-700"}`}>
                  {tip}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const PortalTraining = ({ isAdmin = false, isClient = false }) => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const trainingSteps = [
    {
      number: 1,
      title: "Log In to Your Portal",
      description: "Access your partner dashboard at partners.creditfirminc.net",
      icon: LogIn,
      tips: [
        "Visit partners.creditfirminc.net and sign in with the email and password you registered with.",
        "If you haven't created an account yet, click \"Create Free Account\" on the landing page at partners.creditfirminc.net to get started.",
        "Forgot your password? Use the \"Forgot Password\" link on the login page to reset it.",
      ],
    },
    {
      number: 2,
      title: "Complete Your Partner Profile",
      description: "Upload your photo, logo, and make sure your contact details are accurate",
      icon: UserCircle,
      tips: [
        "Head to \"My Partner Profile\" in the left sidebar.",
        "Upload your professional photo and company logo to build trust with referred clients.",
        "Make sure your contact details (email, phone) are accurate — they appear on your clients' records.",
        "You can update your email address from the Account Settings page at any time.",
      ],
    },
    {
      number: 3,
      title: "Refer Your First Client",
      description: "Two fast ways to send referrals into the system",
      icon: UserPlus,
      image: QUICK_ADD_IMG,
      tips: [
        "Quick Add (Fastest): Go to partners.creditfirminc.net and click the \"Quick Add Client\" button in the top-right corner of the page. Enter the client's first name, last name, and phone number, select yourself as the referring affiliate, and hit submit. That's it — your client is in the system.",
        "Inside the Portal: Click \"Add Client\" in the left sidebar. Fill in the client details, assign yourself and a counselor, add notes, and submit.",
        "You can also add joint applicants and upload documents right from the Add Client page.",
        "Every submitted client automatically receives the \"cfi::referral::new\" tag for CRM workflows.",
        "Betenbough Reps: Use the \"Quick Add Client\" button at partners.creditfirminc.net/refer, or use the \"BH Client\" tab inside the portal's Add Client page to submit with Betenbough-specific fields.",
      ],
    },
    {
      number: 4,
      title: "Track Progress on Your Dashboard",
      description: "See your entire book of business at a glance",
      icon: LayoutDashboard,
      tips: [
        "Your Dashboard shows key counts: My Clients, In Progress, Loan Ready, and New Referrals.",
        "The Lead Status & Stages chart gives a visual breakdown of your client pipeline.",
        "Loan Ready Graduation Stages track clients from enrollment through \"Closed on Home\".",
        "Click any count or stage on the dashboard to jump directly to that filtered list of clients.",
      ],
    },
    {
      number: 5,
      title: "Open a Client Folder",
      description: "View credit scores, game plan recap, restoration progress, and more",
      icon: FolderOpen,
      tips: [
        "Go to \"My Clients\" and click any client's name to open their detailed folder.",
        "View Credit Scores — current Experian, Equifax, and TransUnion scores.",
        "Check the Game Plan Recap — score projections, goals, goal deadline, and next move.",
        "Review Restoration Progress — current round, round date, and days until next update.",
        "Read Case Manager Notes — detailed review of utilization, payment history, and strategy.",
        "Use the CFI Assistant — an AI-powered tool that answers questions about the client's file.",
      ],
    },
    {
      number: 6,
      title: "Search & Filter Clients",
      description: "Quickly find any client by name, email, phone, or status",
      icon: Search,
      tips: [
        "Use the search bar in the sidebar or on the \"My Clients\" page to find any client instantly.",
        "Filter by Lead Status, Loan Ready Stage, Graduation Stage, Counselor, or Affiliate.",
        "Combine search and filters to narrow results — e.g., \"Loan Ready\" clients assigned to you.",
      ],
    },
  ];

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={isAdmin} isClient={isClient} />

      <main className="flex-1 min-w-0 md:ml-64">
        {/* Header */}
        <header className="header-glass sticky top-0 z-30 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
                data-testid="mobile-menu-toggle"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className={`text-xl md:text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                  Portal Training
                </h1>
                <p className={`text-sm ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
                  Learn how to use the Credit Firm Inc. Partner Portal
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-8 max-w-4xl mx-auto">
          {/* Video Section */}
          <Card
            className={`overflow-hidden ${isDarkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-slate-200"}`}
            data-testid="training-video-card"
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#357c2a] to-[#265324] flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className={isDarkMode ? "text-white" : "text-slate-900"}>
                    Partner Portal Introduction
                  </CardTitle>
                  <p className={`text-sm ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
                    17 min &bull; Presented by Dennis Hubbard
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <video
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full bg-black"
                  preload="metadata"
                  data-testid="training-video-player"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </CardContent>
          </Card>

          {/* Step-by-Step Guide */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                Step-by-Step Guide
              </h2>
              <div className={`h-px flex-1 ${isDarkMode ? "bg-zinc-800" : "bg-slate-200"}`} />
            </div>
            <div className="space-y-3">
              {trainingSteps.map((step) => (
                <TrainingStep key={step.number} {...step} />
              ))}
            </div>
          </div>

          {/* Help Banner */}
          <Card className="bg-gradient-to-r from-[#265324] to-[#1a3d18] text-white border-0" data-testid="training-help-banner">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold mb-1">Need More Help?</h3>
                  <p className="text-white/80 text-sm">
                    Contact our partner support team for any questions about using the portal.
                  </p>
                </div>
                <a
                  href="tel:432-599-4880"
                  className="flex-shrink-0 px-5 py-2.5 bg-white text-[#357c2a] font-semibold rounded-lg hover:bg-[#84c77f] transition-colors text-sm"
                  data-testid="training-call-support"
                >
                  Call (432) 599-4880
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PortalTraining;
